<template>
  <validation-provider ref="provider" v-slot="{ errors, valid }" :vid="vid || $props.label" :name="$attrs.name || $props.label" :rules="rules" tag="div" class="field" :class="[rootClasses, fieldType(), checkForErrors($refs.provider)]" :mode="mode">
    <div v-if="horizontal" class="field-label" :class="[customClass, fieldLabelSize]">
      <label v-if="label" :for="labelFor" :class="customClass" class="block label text-sm font-bold mb-2">
        {{ label }}
      </label>
    </div>
    <template v-else>
      <div class="flex items-center mb-2">
        <label v-if="label" :for="labelFor" :class="customClass" class="block label font-bold mb-0">
          {{ label }}
        </label>
        <d-tooltip :label="helpMessage" class="ml-2" v-if="helpMessage">
          <d-icon icon="question-circle" />
        </d-tooltip>
      </div>
    </template>
    <d-field-body v-if="horizontal" :message="formattedMessage" :type="checkForErrors($refs.provider)">
      <slot />
    </d-field-body>
    <template v-else>
      <slot />
    </template>
    <p v-show="formattedMessage && !horizontal && (!$refs.provider || $refs.provider.errors || $refs.provider.errors.length === 0)" class="help" :class="checkForErrors($refs.provider)" v-html="formattedMessage" />
    <p v-if="$refs.provider && $refs.provider.errors && $refs.provider.errors.length > 0" class="help" :class="checkForErrors($refs.provider)" v-html="$refs.provider.errors[0]" />
  </validation-provider>
</template>

<script>
  import DFieldBody from './DFieldBody.vue';
  import DIcon from '../DIcon/DIcon.vue';

  export default {
    name: 'DField',
    components: {
      DFieldBody,
      DIcon,
    },
    props: {
      /**
       * VeeValidate specific VID. Closely mimics the `name` attribute and only required for validation
       */
      vid: {
        type: String,
      },

      /**
       * VeeValidate specific rules. Only required for validation
       */
      rules: {
        type: [Object, String],
        default: '',
      },

      /**
       * VeeValidate specific. Custom is our modified version of `eager`
       * @params aggressive, lazy, passive, eager, custom
       */
      mode: {
        type: String,
        default: 'custom',
      },
      helpMessage: String,
      type: [String, Object],
      label: String,
      labelFor: String,
      message: [String, Array, Object],
      grouped: Boolean,
      groupMultiline: Boolean,
      position: String,
      expanded: Boolean,
      horizontal: Boolean,
      addons: {
        type: Boolean,
        default: true,
      },
      customClass: String,
    },
    data() {
      return {
        newType: this.type,
        newMessage: this.message,
        fieldLabelSize: null,
        _isField: true, // Used internally by Input and Select
      };
    },
    computed: {
      rootClasses() {
        return [this.newPosition, {
          'is-expanded': this.expanded,
          'is-grouped-multiline': this.groupMultiline,
          'is-horizontal': this.horizontal,
        }];
      },
      /**
       * This is not kept like the others (is-small, etc.),
       * because since 'has-addons' is set automatically it
       * doesn't make sense to teach users what addons are exactly.
       */
      newPosition() {
        if (this.position === undefined) return;
        const position = this.position.split('-');
        if (position.length < 1) return;
        const prefix = this.grouped
          ? 'is-grouped-'
          : 'has-addons-';
        if (this.position) return prefix + position[1];
      },

      computedType() {
        return this.newType || '';
      },

      /**
       * Formatted message in case it's an array
       * (each element is separated by <br> tag)
       */
      formattedMessage() {
        if (typeof this.newMessage === 'string') {
          return this.newMessage;
        }
        const messages = [];
        if (Array.isArray(this.newMessage)) {
          this.newMessage.forEach((message) => {
            if (typeof message === 'string') {
              messages.push(message);
            } else {
              for (const key in message) {
                if (message[key]) {
                  messages.push(key);
                }
              }
            }
          });
        } else {
          for (const key in this.newMessage) {
            if (this.newMessage[key]) {
              messages.push(key);
            }
          }
        }
        return messages.filter((m) => { if (m) return m; }).join(' <br> ');
      },
    },
    watch: {
      /**
       * Set internal type when prop change.
       */
      type(value) {
        this.newType = value;
      },
      /**
       * Set internal message when prop change.
       */
      message(value) {
        this.newMessage = value;
      },
    },
    mounted() {
      if (this.horizontal) {
        const elements = this.$el.querySelectorAll('.input, .select, .button, .textarea');
        if (elements.length > 0) {
          this.fieldLabelSize = 'is-normal';
        }
      }
    },
    methods: {
      /**
       * Field has addons if there are more than one slot
       * (element / component) in the Field.
       * Or is grouped when prop is set.
       * Is a method to be called when component re-render.
       */
      checkForErrors(provider) {
        if (provider && provider.errors && provider.errors.length > 0) {
            return {
              'is-error': provider.errors[0],
              'is-success': provider.valid,
            };
          }
      },

      fieldType() {
        if (this.grouped) return 'is-grouped';
        let renderedNode = 0;
        if (this.$slots.default) {
          renderedNode = this.$slots.default
            .reduce((i, node) => (node.tag ? i + 1 : i), 0);
        }
        if (
          renderedNode > 1
          && this.addons
          && !this.horizontal
        ) {
          return 'has-addons';
        }
      },
    },
  };
</script>